import { useCallback, useEffect, useRef, useState } from 'react';
import {
  encryptNfcRecords,
  decryptNfcRecords,
  prepareNfcrwHandshake,
  prepareNfcrwScan,
  getNfcEncryptedWriteUniversalLink,
  readAndClearNfcrwCallbackFromLocalStorage,
  clearNfcrwCallbackFromLocalStorage,
} from './nativeApp';
import { nativeAppRecordsToWebNfcRecords, webNfcRecordsToNativeAppRecords } from './utils';

// iPhone アプリ（nfcReaderWriter）とのやり取りを、画面ごとに書かずに済ませるためのフック。
// 取り決め（Universal Link・暗号・localStorage での受け渡し）は nativeApp.js が持つ。
// nativeApp.js とこのファイルは、モナダム（KnightsOfMonadom）の front/src/ の同じ名前のファイルを写したもの。
// nativeApp.js はモナダムと一字一句同じ（monashell の nativeApp.js（babea40）に notifyNativeAppWriteResult を足したもの）。取り決めを変えるときは monashell・モナダム・nfcReaderWriter とそろえる。
// ここは monashell の Setting.js / LoginUI.js / UserRegistration.js が画面ごとに書いている
// 「リンクを前もって作る・戻ってきた結果を受け取る・鍵を作り直す」を一つにまとめたもの。
// 画面の中ではレコードを Web NFC の形で扱う。アプリの形（{ kind, value }）との変換は、このフックの中（アプリとの境目）だけで行う。

// ネイティブアプリから戻ってきたこと（元タブが見えるようになった・フォーカスが戻った）を知らせるイベントリスナーを設定し、
// 外れるときにクリーンアップする。結果そのものは、consume が localStorage から自分で読む
// リスナーは enabled のあいだだけ設定する（iPhone 以外の端末や、iPhone アプリを使わないウォレットでは設定しない）
function useNativeAppReturnListeners(consume, enabled) {
  const consumeRef = useRef(consume);

  consumeRef.current = consume;

  useEffect( () => {
    if (!enabled) {
      return;
    }

    const handleFocus = () => {
      consumeRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        consumeRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled]);
}

// 画面を離れると鍵ペアと state も消えるため、消費できなくなったコールバックを捨てる（monashell の DisplayMnemonic と同じ）
// 受け渡しのキーは一つなので、同じオリジンの別タブの待ち結果も消す。2026-10-09 じゅんじゅん: 問題が多発したら検討する。
function useClearCallbackOnUnmount(clearOnUnmount) {
  const clearOnUnmountRef = useRef(clearOnUnmount);

  clearOnUnmountRef.current = clearOnUnmount;

  useEffect( () => {
    return () => {
      if (clearOnUnmountRef.current) {
        clearNfcrwCallbackFromLocalStorage();
      }
    };
  }, []);
}

// 読み込み（1 往復）
// 返り値の scanLink を <a href> に入れ、onClick で armScan を呼ぶ。
// armScan はリンクが未準備なら遷移を止めて false を返す。
// onRecords({ records, intent }) は復号できたとき（records は Web NFC の形）、onFailure({ reason, error, intent }) は失敗したときに呼ぶ。
// reason: 'appError'（アプリが error を返した、または応答が欠けている）| 'decryptFailed'
function useNfcrwScan({ enabled = true, language, onRecords, onFailure, clearOnUnmount = false }) {
  const [scanLink, setScanLink] = useState(null);
  const scanLinkRef = useRef(null);
  const sessionRef = useRef(null); // { session, state }
  const preparingRef = useRef(false);
  const requestRef = useRef(null); // { intent }。リンクが押されると入る
  const enabledRef = useRef(enabled);
  const languageRef = useRef(language);
  const onRecordsRef = useRef(onRecords);
  const onFailureRef = useRef(onFailure);

  enabledRef.current = enabled;
  languageRef.current = language;
  onRecordsRef.current = onRecords;
  onFailureRef.current = onFailure;

  // タップ時に同期的に href を確定できるよう、暗号化スキャン用 Universal Link を事前に組み立てておく
  const prepare = useCallback( () => {
    if (sessionRef.current || preparingRef.current) {
      return;
    }

    preparingRef.current = true;

    prepareNfcrwScan({ language: languageRef.current })
    .then( ({ session, state, scanLink }) => {
      sessionRef.current = { session, state };
      scanLinkRef.current = scanLink;
      setScanLink(scanLink);
    })
    .finally( () => {
      preparingRef.current = false;
    });
  }, []);

  useEffect( () => {
    if (enabled) {
      prepare();
    }
  }, [enabled, language, prepare]);

  const armScan = useCallback( (event, { intent } = {}) => {
    if (!scanLinkRef.current || !sessionRef.current) {
      event?.preventDefault();
      return false;
    }

    requestRef.current = { intent };

    return true;
  }, []);

  useNativeAppReturnListeners( async () => {
    if (!enabledRef.current || !requestRef.current || !sessionRef.current) {
      return;
    }

    const { session, state } = sessionRef.current;
    const payload = readAndClearNfcrwCallbackFromLocalStorage({ state });

    if (!payload) {
      return;
    }

    const { intent } = requestRef.current;

    // セッションは使い捨てとし、すぐに次回用を作り直す
    requestRef.current = null;
    sessionRef.current = null;
    scanLinkRef.current = null;
    setScanLink(null);
    prepare();

    if (payload.error || !payload.pubkey || !payload.ciphertext) {
      onFailureRef.current?.({ reason: 'appError', error: payload.error, intent });
      return;
    }

    let records;

    try {
      ({ records } = await decryptNfcRecords({
        keyPair: session.keyPair,
        browserPublicKey: session.browserPublicKey,
        appPublicKeyB64: payload.pubkey,
        ciphertextB64: payload.ciphertext,
        state,
      }));
    }
    catch {
      onFailureRef.current?.({ reason: 'decryptFailed', intent });
      return;
    }

    await onRecordsRef.current?.({ records: nativeAppRecordsToWebNfcRecords({ records }), intent });
  }, enabled);

  useClearCallbackOnUnmount(clearOnUnmount);

  return { scanLink, armScan };
}

// 書き込み（2 往復）
// 1/2: handshakeLink を <a href> に入れ、onClick で armHandshake を呼ぶ。アプリは NFC に触れず、公開鍵だけ返す。
// 戻ると buildRecords({ intent }) でレコード（Web NFC の形。formatToKeyCardStyle など）を作り、アプリの形に直して暗号化し、writeLink ができる（onReadyToWrite が呼ばれる）。
// 2/2: writeLink を <a href> に入れ、onClick で armWrite を呼ぶ。結果は onResult({ ok, error })。
// 鍵交換セッションは intent（'plain' | 'keycard' など）の間で共有する。最後に完了した鍵交換の intent でレコードを作る（monashell と同じ）。
// source には書き込む中身（ニーモニックなど）を渡す。2/2 のリンクを作ったあとで source が変わったら、
// 古い中身の暗号文を書かないよう、鍵交換からやり直させる（monashell の動きを直した点。設計書の H-2）。
function useNfcrwWrite({ enabled = true, language, source, buildRecords, onReadyToWrite, onResult, clearOnUnmount = false }) {
  const [handshakeLink, setHandshakeLink] = useState(null);
  const [write, setWrite] = useState({ link: null, intent: null });
  const handshakeLinkRef = useRef(null);
  const writeLinkRef = useRef(null);
  const writeIntentRef = useRef(null); // 2/2 のリンクを作ったときの intent
  const sessionRef = useRef(null); // { session, state }
  const preparingRef = useRef(false);
  const intentRef = useRef(null); // 最後に押された 1/2 の intent
  const requestedRef = useRef(false); // 1/2 か 2/2 が押されると true
  const builtSourceRef = useRef(undefined); // 2/2 のリンクを作ったときの source
  const enabledRef = useRef(enabled);
  const languageRef = useRef(language);
  const sourceRef = useRef(source);
  const buildRecordsRef = useRef(buildRecords);
  const onReadyToWriteRef = useRef(onReadyToWrite);
  const onResultRef = useRef(onResult);

  enabledRef.current = enabled;
  languageRef.current = language;
  sourceRef.current = source;
  buildRecordsRef.current = buildRecords;
  onReadyToWriteRef.current = onReadyToWrite;
  onResultRef.current = onResult;

  // タップ時に同期的に href を確定できるよう、ハンドシェイク用 Universal Link を事前に組み立てておく
  const prepare = useCallback( () => {
    if (sessionRef.current || preparingRef.current) {
      return;
    }

    preparingRef.current = true;

    prepareNfcrwHandshake({ language: languageRef.current })
    .then( ({ session, state, handshakeLink }) => {
      sessionRef.current = { session, state };
      handshakeLinkRef.current = handshakeLink;
      setHandshakeLink(handshakeLink);
    })
    .finally( () => {
      preparingRef.current = false;
    });
  }, []);

  // セッションを終了し、次回用の鍵ペアを作り直す
  const reset = useCallback( () => {
    sessionRef.current = null;
    intentRef.current = null;
    requestedRef.current = false;
    builtSourceRef.current = undefined;
    handshakeLinkRef.current = null;
    writeLinkRef.current = null;
    writeIntentRef.current = null;
    setHandshakeLink(null);
    setWrite({ link: null, intent: null });

    if (enabledRef.current) {
      prepare();
    }
  }, [prepare]);

  useEffect( () => {
    if (enabled) {
      prepare();
    }
  }, [enabled, language, prepare]);

  // 2/2 のリンクを作ったあとで書き込む中身が変わったら、鍵交換からやり直させる
  useEffect( () => {
    if (writeLinkRef.current !== null && builtSourceRef.current !== source) {
      reset();
    }
  }, [source, reset]);

  const armHandshake = useCallback( (event, { intent } = {}) => {
    if (!handshakeLinkRef.current || !sessionRef.current) {
      event?.preventDefault();
      return false;
    }

    intentRef.current = intent;
    requestedRef.current = true;

    return true;
  }, []);

  const armWrite = useCallback( (event) => {
    if (!writeLinkRef.current || !sessionRef.current) {
      event?.preventDefault();
      return false;
    }

    requestedRef.current = true;

    return true;
  }, []);

  useNativeAppReturnListeners( async () => {
    if (!enabledRef.current || !requestedRef.current || !sessionRef.current) {
      return;
    }

    const { session, state } = sessionRef.current;
    const payload = readAndClearNfcrwCallbackFromLocalStorage({ state });

    if (!payload) {
      return;
    }

    requestedRef.current = false;

    // 鍵交換の応答: アプリ公開鍵から AES 鍵を導出してレコードを暗号化し、書き込みフェーズへ進む
    // セッションは共有のため、最後に完了した鍵交換の intent でレコードを作り直し、writeLink を置き換える。
    if (payload.pubkey) {
      const intent = intentRef.current;
      const sourceToWrite = sourceRef.current;
      let ciphertextB64;

      try {
        const records = webNfcRecordsToNativeAppRecords({ records: await buildRecordsRef.current({ intent }) });

        ciphertextB64 = await encryptNfcRecords({
          keyPair: session.keyPair,
          browserPublicKey: session.browserPublicKey,
          appPublicKeyB64: payload.pubkey,
          records,
          state,
        });
      }
      catch {
        reset();
        onResultRef.current?.({ ok: false, error: 'buildFailed', intent });
        return;
      }

      // 暗号化しているあいだにセッションが変わっていたら（中身が変わってやり直しになったなど）、捨てる
      if (sessionRef.current?.state !== state) {
        return;
      }

      const link = getNfcEncryptedWriteUniversalLink({ ciphertextB64, state, language: languageRef.current });

      builtSourceRef.current = sourceToWrite;
      writeLinkRef.current = link;
      writeIntentRef.current = intent;
      setWrite({ link, intent });

      // 中身が暗号化の途中で変わっていたら、作ったリンクは捨てる
      if (sourceRef.current !== sourceToWrite) {
        reset();
        return;
      }

      onReadyToWriteRef.current?.({ intent });

      // state は書き込み完了通知まで維持する
      return;
    }

    // 書き込み結果: セッションを終了し、次回用の鍵ペアを作り直す
    const intent = writeIntentRef.current ?? intentRef.current;

    reset();

    if (payload.nfc) {
      onResultRef.current?.({ ok: true, intent });
    }
    else {
      onResultRef.current?.({ ok: false, error: payload.error, intent });
    }
  }, enabled);

  useClearCallbackOnUnmount(clearOnUnmount);

  return {
    handshakeLink,
    writeLink: write.link,
    writeIntent: write.intent,
    armHandshake,
    armWrite,
    reset,
  };
}

export {
  useNfcrwScan,
  useNfcrwWrite,
};
